import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock, Smartphone, ClipboardList, ShieldCheck, BarChart3, ChevronDown, MapPin, Fingerprint } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LandingHeader } from '../../components/landing/LandingHeader';
import { CTASection } from '../../components/landing/CTASection';

export default function TimeTracking() {
  useEffect(() => {
    document.title = 'Time Tracking Software | ReStaff';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Track employee work hours, manage timesheets, and improve payroll accuracy with ReStaff.';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="time-tracking-page">
      <LandingHeader />

      {/* HERO */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Manrope' }}>
            Time Tracking Software for Employees
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Track employee clock-ins and clock-outs, generate payroll-ready timesheets, and get accurate work hour data — from any device.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white text-base px-8" data-testid="tracking-hero-cta">
                Start Free Trial <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
          <p className="mt-3 text-sm text-slate-400">30-day free trial &middot; Cancel anytime, no questions asked</p>
        </div>
      </section>

      {/* WHAT IT IS */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5" style={{ fontFamily: 'Manrope' }}>
            What is Time Tracking Software?
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Time tracking software records when employees start and end their shifts, calculates hours worked, and produces timesheets ready for payroll. ReStaff provides PIN-based kiosk clock-in, mobile clock-in, and real-time attendance tracking — giving managers accurate data without manual timesheets.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Employees Clock In', desc: 'Staff clock in using a PIN on the kiosk, or from their phone. Clock events are recorded to the second.', icon: Fingerprint },
              { step: '2', title: 'Hours Are Tracked', desc: 'ReStaff automatically calculates total hours, breaks, and overtime. Managers see attendance in real time.', icon: Clock },
              { step: '3', title: 'Export for Payroll', desc: 'Review timesheets, approve or adjust entries, and export payroll-ready reports with one click.', icon: ClipboardList },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.step} className="bg-white border border-slate-100 rounded-xl p-6 text-center" data-testid={`tracking-step-${s.step}`}>
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Time Tracking Features
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
            {[
              { title: 'PIN-Based Kiosk Clock-In', desc: 'Set up a shared tablet as a time clock kiosk. Employees enter their PIN to clock in and out — fast, simple, and accurate.', icon: Fingerprint },
              { title: 'Mobile Clock-In', desc: 'Employees clock in from their phone when working remotely or at different locations. No hardware needed.', icon: Smartphone },
              { title: 'Payroll-Ready Timesheets', desc: 'Timesheets are generated automatically from clock events. Review, approve, and export hours by employee or location.', icon: ClipboardList },
              { title: 'Real-Time Attendance', desc: 'See who is clocked in right now, who is late, and who missed their shift — all updated in real time on the dashboard.', icon: Clock },
              { title: 'Overtime Tracking', desc: 'ReStaff calculates overtime automatically based on your rules. Get alerts when employees approach thresholds.', icon: ShieldCheck },
              { title: 'Multi-Location Tracking', desc: 'Each location has its own time clock and timesheets. Compare hours and labor costs across all branches.', icon: MapPin },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white border border-slate-100 rounded-xl p-6 hover:border-orange-200 hover:shadow-sm transition-all" data-testid={`tracking-feature-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Why Use ReStaff for Time Tracking
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Eliminate manual timesheets and paper punch cards',
              'Get accurate work hours for every employee, every shift',
              'Catch overtime before it hits payroll',
              'Export payroll-ready data in one click',
              'Track attendance across multiple locations',
              'Reduce time theft with PIN-verified clock events',
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-4">
                <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700">{b}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slate-500 text-center leading-relaxed">
            Pair time tracking with ReStaff <Link to="/employee-scheduling" className="text-orange-500 hover:underline font-medium">employee scheduling software</Link>, <Link to="/timesheet-software" className="text-orange-500 hover:underline font-medium">timesheet software</Link>, <Link to="/labor-cost-management" className="text-orange-500 hover:underline font-medium">labor cost management</Link>, and <Link to="/task-management" className="text-orange-500 hover:underline font-medium">task management software</Link> for a complete workforce solution. Built for <Link to="/construction" className="text-orange-500 hover:underline font-medium">construction teams</Link>, <Link to="/agencies" className="text-orange-500 hover:underline font-medium">agencies</Link>, and more.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              { q: 'What is employee time tracking software?', a: 'Employee time tracking software records when employees clock in and out, calculates hours worked, and generates timesheets. It replaces manual tracking with accurate, automated records ready for payroll.' },
              { q: 'How do employees clock in with ReStaff?', a: 'Employees can clock in using a PIN on a shared kiosk tablet, or directly from their mobile phone. Both methods record the exact time and are visible to managers in real time.' },
              { q: 'Are timesheets automatically generated?', a: 'Yes. ReStaff builds timesheets automatically from clock-in and clock-out events. Managers review and approve entries, then export payroll-ready reports.' },
              { q: 'Can I track hours across multiple locations?', a: 'Yes. Each location has its own time clock and timesheet data. Managers and owners can view and compare hours, costs, and attendance across all locations from one dashboard.' },
            ].map((item, i) => (
              <details key={i} className="group bg-slate-50 border border-slate-100 rounded-xl" data-testid={`tracking-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-semibold text-slate-900 select-none">
                  {item.q}
                  <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 flex-shrink-0 ml-3" />
                </summary>
                <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
